import anime from 'animejs';
import type { HTMLAttributes } from 'react';
import { useInViewEffect } from 'react-hook-inview';

import type { SolutionEmpowersType } from '../constants/solutions';

type EmpowerSectionType = {
  title: string;
  features: SolutionEmpowersType[];
} & HTMLAttributes<HTMLDivElement>;

const EmpowerSection = ({ title, features, ...props }: EmpowerSectionType) => {
  const sectionRef = useInViewEffect(
    ([entry], observer) => {
      if (entry.isIntersecting) {
        observer.unobserve(entry.target);

        anime
          .timeline({ loop: false })
          .add(
            {
              targets: '#solution-empower-section h2',
              opacity: [0, 1],
              translateY: [100, 0],
              duration: 1000,
              easing: 'easeOutExpo',
            },
            '-=250',
          )
          .add(
            {
              targets: '#empower-list li',
              opacity: [0, 1],
              translateY: [100, 0],
              duration: 750,
              easing: 'easeOutExpo',
              delay: anime.stagger(100),
            },
            '-=750',
          );
      }
    },
    { threshold: 0.2 },
  );

  return (
    <section
      ref={sectionRef}
      id="solution-empower-section"
      className="w-full rounded-[100%_100%_0_0/5%_5%_0_0] bg-brand-50 lg:rounded-[100%_100%_0_0/20%_20%_0_0]"
      {...props}
    >
      <div className="relative isolate mx-auto max-w-container overflow-hidden px-10 py-20 lg:px-20 lg:py-40">
        <div className=" mb-12 text-center">
          <h2 className="text-xl font-semibold text-zinc-900 [text-wrap:pretty] sm:text-2xl lg:text-4xl/relaxed">
            {title}
          </h2>
        </div>
        <ul
          id="empower-list"
          className="m-0 grid w-fit grid-cols-1 gap-x-36 gap-y-6 sm:mx-auto md:grid-cols-2 lg:w-full lg:grid-cols-2 lg:gap-y-[3.75rem]"
        >
          {features.map((feature) => (
            <li key={feature.key} className="flex items-center gap-5">
              <span>
                {feature.Icon && (
                  <feature.Icon className="text-white max-sm:h-8 max-sm:w-8" />
                )}
              </span>
              <div className="lg:space-y-3">
                <h3 className="text-base font-medium text-zinc-900 md:text-lg lg:text-2xl">
                  {feature.title}
                </h3>
                <p className="text-[.63rem] text-zinc-500 md:text-sm lg:text-lg">
                  {feature.content}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default EmpowerSection;
